import { useMemo } from 'react';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useSorting } from '@atlassian/jira-business-filters/src/services/sorting/index.tsx';
import type { GenericVariables } from '@atlassian/jira-business-list-handlers/src/common/types';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { useRankIssue as useRankIssueService } from '@atlassian/jira-business-list-handlers/src/controllers/rank-issue/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { LIST_VIEW_QUERY } from '../../services/get-list-view-issues/gql';
import type { ListViewControllerData } from '../list-view-issues/types';
import { useRankParent } from '../rank-parent';
import { useIssuesInput } from '../use-issues-input';
import {
	RANK_ISSUE_EXPERIENCE,
	RANK_ITEM_FAIL_GENERIC_FLAG,
	RANK_ITEM_FAIL_403_FLAG,
	RANK_ITEM_FAIL_404_FLAG,
} from './constants';

export const createOnRankError = () => (error: Error) => {
	fireErrorAnalytics({
		meta: {
			id: 'rankIssuelistViewRestQuery',
			packageName: 'jiraBusinessList',
			teamName: 'keystone',
		},
		attributes: {
			message: 'Failed to rank issue in list view via Rest',
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
	});
};

export const useRankIssue = () => {
	const { showFlag } = useFlagService();
	const [{ sortBy }] = useSorting();
	const cloudId = useCloudId();
	const isBlockedBySorting = sortBy !== undefined && sortBy !== null;
	const [{ rankParentIssueId }] = useRankParent();
	const input = useIssuesInput({
		groupIssueId: isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
		parentIssueId: !isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const startExperience = useExperienceStart(RANK_ISSUE_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(RANK_ISSUE_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(RANK_ISSUE_EXPERIENCE);

	const onError = useMemo(() => createOnRankError(), []);
	const { loading, rankListViewIssue } = useRankIssueService({
		query: LIST_VIEW_QUERY,
		variables: { ...getVariables(input), cloudId },
		onError: (error: FetchError) => {
			onError(error);
			if (error.statusCode === 403) {
				showFlag(RANK_ITEM_FAIL_403_FLAG);
			} else if (error.statusCode === 404) {
				showFlag(RANK_ITEM_FAIL_404_FLAG);
			} else {
				showFlag(RANK_ITEM_FAIL_GENERIC_FLAG);
				if (error.statusCode >= 500) {
					markExperienceFailed('list issue rank failed', error);
					return;
				}
			}
			markExperienceSuccess();
		},
		onSuccess: () => {
			markExperienceSuccess();
			fireTrackAnalytics(createAnalyticsEvent({}), 'issue update', {
				fieldKey: 'rank',
			});
			fireUIAnalytics(createAnalyticsEvent({}), 'listRankIssue success');
		},
		onStart: () => {
			startExperience();
		},
	});
	const rankIssue = ({
		newIndex,
		oldIndex,
		issues,
		issueToRank,
		variables,
		skipUpdateCache,
		instruction,
		relativeIssueOverride,
	}: {
		newIndex: number;
		oldIndex: number;
		issues: ListViewControllerData[];
		issueToRank?: ListViewControllerData;
		variables?: GenericVariables;
		skipUpdateCache?: boolean;
		instruction?: string;
		relativeIssueOverride?: ListViewControllerData;
	}) => {
		const params = {
			oldIndex,
			newIndex,
			issues,
			issueToRank,
			variables,
			skipUpdateCache,
			instruction,
			relativeIssueOverride,
		};
		rankListViewIssue(params);
	};

	return { rankIssue, isBlockedBySorting, loading };
};
